import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Video, Play, Loader2, CheckCircle, XCircle, RefreshCw, 
  Zap, Send, Clock, Film, ChevronDown, ChevronUp,
  Sparkles, Copy, Wand2
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

/* -------------------------------------------------------------------------
 * PromptStudio — LLM-driven structured HeyGen script generator.
 * Fills the "same video over and over" gap by producing a 5-scene arc:
 *   Hook → B-roll → Teach → Example → CTA
 * per lesson topic, subject, grade level, and duration. Copy-to-clipboard
 * per scene + a "Copy Full Script" that concatenates everything for HeyGen.
 * ------------------------------------------------------------------------- */
const SUBJECTS = ['Math', 'ELA', 'Science', 'Social Studies', 'Basketball', 'Chess', 'Culture', 'Recruiting', 'Orientation'];
const TONES    = ['Authoritative & Encouraging', 'High-Energy', 'Analytical', 'Reflective', 'Cinematic'];
const DURATIONS = [30, 45, 60, 90, 120, 180];

const PromptStudio = () => {
  const [form, setForm] = useState({
    lesson_topic: '',
    subject: 'Chess',
    grade_level: '7',
    duration_seconds: 60,
    tone: 'Authoritative & Encouraging',
    student_name: '',
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const generate = async () => {
    if (!form.lesson_topic.trim()) { toast.error('Please describe the lesson topic'); return; }
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post('/academy/admin/heygen/prompt-studio', form);
      setResult(res.data.record);
      toast.success('Script generated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Generation failed');
    } finally { setBusy(false); }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Clipboard blocked — please copy manually');
    }
  };

  const buildFullScript = () => {
    if (!result?.output) return '';
    const o = result.output;
    const lines = [
      `SAA · ${o.title}`,
      `${o.one_line_summary}`,
      `Total: ${o.total_seconds}s · Aspect: ${o.recommended_aspect}`,
      ``,
      `STYLE NOTES: ${o.on_screen_style_notes}`,
      ``,
    ];
    o.scenes.forEach((s) => {
      lines.push(`— SCENE ${s.scene_number} · ${s.type.toUpperCase()} · ${s.duration_seconds}s —`);
      if (s.avatar_script) lines.push(`AVATAR: ${s.avatar_script}`);
      lines.push(`VISUAL: ${s.visual_direction}`);
      if (s.text_overlay) lines.push(`ON-SCREEN: ${s.text_overlay}`);
      if (s.audio_direction) lines.push(`AUDIO: ${s.audio_direction}`);
      lines.push('');
    });
    if (o.hey_gen_operator_notes) {
      lines.push(`OPERATOR NOTES:`);
      lines.push(o.hey_gen_operator_notes);
    }
    return lines.join('\n');
  };

  return (
    <Card className="bg-gradient-to-br from-purple-950/40 to-black border-purple-800/60 mb-8" data-testid="prompt-studio">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Wand2 className="w-5 h-5 text-purple-400" />
          Prompt Studio · Structured Script Generator
        </CardTitle>
        <p className="text-xs text-zinc-400 mt-1">
          Give it a lesson topic — get back a five-scene HeyGen script (Hook → B-roll → Teach → Example → CTA) so every video has real variety instead of the same avatar-only shot.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block md:col-span-2">
            <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mb-1">Lesson Topic *</div>
            <input
              type="text"
              value={form.lesson_topic}
              onChange={(e) => update({ lesson_topic: e.target.value })}
              placeholder="e.g. Escaping the Noah's Ark trap in chess"
              className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none"
              data-testid="ps-topic"
            />
          </label>
          <label className="block">
            <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mb-1">Subject</div>
            <select value={form.subject} onChange={(e) => update({ subject: e.target.value })}
                    className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none">
              {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mb-1">Grade Level</div>
            <input type="text" value={form.grade_level} onChange={(e) => update({ grade_level: e.target.value })}
                   className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" />
          </label>
          <label className="block">
            <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mb-1">Duration</div>
            <select value={form.duration_seconds} onChange={(e) => update({ duration_seconds: parseInt(e.target.value, 10) })}
                    className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none">
              {DURATIONS.map((d) => <option key={d} value={d}>{d}s</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mb-1">Tone</div>
            <select value={form.tone} onChange={(e) => update({ tone: e.target.value })}
                    className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none">
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] font-black tracking-widest text-zinc-500 uppercase mb-1">Personalize For (optional)</div>
            <input type="text" value={form.student_name} onChange={(e) => update({ student_name: e.target.value })}
                   placeholder="e.g. Maddox — will address student by name in hook & CTA"
                   className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" />
          </label>
        </div>

        <Button onClick={generate} disabled={busy || !form.lesson_topic.trim()}
                className="bg-purple-500 hover:bg-purple-400 text-black font-black tracking-widest uppercase text-xs disabled:opacity-40"
                data-testid="ps-generate">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {busy ? 'Generating…' : 'Generate 5-Scene Script'}
        </Button>

        {result && (
          <div className="mt-6 border-t border-zinc-800 pt-6 space-y-4" data-testid="ps-result">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-black tracking-widest text-purple-300 uppercase">Generated Script</div>
                <div className="text-xl font-black text-white mt-1">{result.output.title}</div>
                <div className="text-xs text-zinc-400 mt-1">{result.output.one_line_summary}</div>
                <div className="text-[10px] text-zinc-500 mt-2 uppercase tracking-widest">
                  {result.output.total_seconds}s · {result.output.recommended_aspect} · {result.output.scenes?.length || 0} scenes
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => copyText(buildFullScript(), 'Full script')}
                      className="border-purple-700 text-purple-300 hover:bg-purple-500/10" data-testid="ps-copy-all">
                <Copy className="w-3 h-3 mr-1" /> Copy Full Script
              </Button>
            </div>

            {result.output.on_screen_style_notes && (
              <div className="bg-black/50 border border-zinc-800 p-3 text-[11px] text-zinc-300">
                <div className="text-[9px] font-black text-purple-300 tracking-widest uppercase mb-1">Style Notes</div>
                {result.output.on_screen_style_notes}
              </div>
            )}

            {result.output.scenes?.map((s) => (
              <div key={s.scene_number} className="border border-zinc-800 bg-black/60 p-4" data-testid={`ps-scene-${s.scene_number}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="text-[9px] font-black tracking-widest text-purple-300 uppercase bg-purple-500/10 border border-purple-500/30 px-2 py-0.5">
                      Scene {s.scene_number}
                    </div>
                    <div className="text-[9px] font-black tracking-widest text-zinc-500 uppercase">
                      {s.type.replace('_', ' ')} · {s.duration_seconds}s
                    </div>
                  </div>
                  {s.avatar_script && (
                    <Button variant="ghost" size="sm" onClick={() => copyText(s.avatar_script, 'Avatar line')}
                            className="text-xs text-zinc-400 hover:text-white h-6">
                      <Copy className="w-3 h-3 mr-1" /> Copy Avatar Line
                    </Button>
                  )}
                </div>
                {s.avatar_script && (
                  <div className="text-sm text-white font-medium leading-relaxed mb-2">&ldquo;{s.avatar_script}&rdquo;</div>
                )}
                <div className="text-[11px] text-zinc-400 leading-relaxed">
                  <span className="text-purple-300 font-black uppercase tracking-widest text-[9px]">Visual · </span>{s.visual_direction}
                </div>
                {s.text_overlay && (
                  <div className="text-[11px] text-amber-300 mt-1">
                    <span className="text-amber-500 font-black uppercase tracking-widest text-[9px]">On-Screen · </span>{s.text_overlay}
                  </div>
                )}
                {s.audio_direction && (
                  <div className="text-[11px] text-zinc-500 mt-1">
                    <span className="text-cyan-300 font-black uppercase tracking-widest text-[9px]">Audio · </span>{s.audio_direction}
                  </div>
                )}
              </div>
            ))}

            {result.output.hey_gen_operator_notes && (
              <div className="bg-amber-950/40 border border-amber-800/40 p-3 text-[11px] text-amber-100 whitespace-pre-line">
                <div className="text-[9px] font-black text-amber-400 tracking-widest uppercase mb-1">HeyGen Operator Notes</div>
                {result.output.hey_gen_operator_notes}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const STATUS_COLORS = {
  processing: 'text-yellow-400',
  completed: 'text-green-400',
  failed: 'text-red-400',
  pending: 'text-gray-400'
};

const STATUS_ICONS = {
  processing: Loader2,
  completed: CheckCircle,
  failed: XCircle,
  pending: Clock
};

const HeyGenAdmin = () => {
  const [scripts, setScripts] = useState([]);
  const [videos, setVideos] = useState({});
  const [generating, setGenerating] = useState({});
  const [polling, setPolling] = useState({});
  const [batchMode, setBatchMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [voiceId, setVoiceId] = useState('8faa09e16e7e48f59d6cc8d7e3c537fc');
  const [expandedScript, setExpandedScript] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [scriptsRes, videosRes] = await Promise.all([
        api.get('/heygen/scripts'),
        api.get('/heygen/videos')
      ]);
      setScripts(scriptsRes.data.scripts || []);
      const videoMap = {};
      (videosRes.data.videos || []).forEach(v => {
        if (!videoMap[v.script_key] || v.created_at > videoMap[v.script_key].created_at) {
          videoMap[v.script_key] = v;
        }
      });
      setVideos(videoMap);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const generateSingle = async (scriptKey) => {
    setGenerating(prev => ({ ...prev, [scriptKey]: true }));
    try {
      const res = await api.post('/heygen/generate', { script_key: scriptKey, voice_id: voiceId });
      const videoId = res.data.video?.video_id;
      toast.success(`Video generation started for ${scriptKey}`);
      if (videoId) pollStatus(scriptKey, videoId);
    } catch (err) {
      toast.error(`Failed to generate: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGenerating(prev => ({ ...prev, [scriptKey]: false }));
    }
  };

  const generateBatch = async () => {
    if (selectedKeys.length === 0) {
      toast.error('Select at least one script');
      return;
    }
    setGenerating(prev => {
      const newState = { ...prev };
      selectedKeys.forEach(k => { newState[k] = true; });
      return newState;
    });
    try {
      const res = await api.post('/heygen/generate-batch', { script_keys: selectedKeys, voice_id: voiceId });
      toast.success(`${res.data.total_submitted} videos submitted for generation`);
      (res.data.videos || []).forEach(v => {
        if (v.video_id) pollStatus(v.key, v.video_id);
      });
      setSelectedKeys([]);
      setBatchMode(false);
    } catch (err) {
      toast.error(`Batch generation failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGenerating(prev => {
        const newState = { ...prev };
        selectedKeys.forEach(k => { newState[k] = false; });
        return newState;
      });
    }
  };

  const pollStatus = (scriptKey, videoId) => {
    setPolling(prev => ({ ...prev, [scriptKey]: true }));
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/heygen/status/${videoId}`);
        const status = res.data.video?.status;
        if (status === 'completed') {
          clearInterval(interval);
          setPolling(prev => ({ ...prev, [scriptKey]: false }));
          setVideos(prev => ({ ...prev, [scriptKey]: { ...res.data.video, script_key: scriptKey } }));
          toast.success(`Video ready: ${scriptKey}`);
          // Auto-apply to curriculum
          try { await api.post(`/heygen/apply-video/${scriptKey}`); } catch {}
        } else if (status === 'failed') {
          clearInterval(interval);
          setPolling(prev => ({ ...prev, [scriptKey]: false }));
          toast.error(`Video failed: ${scriptKey}`);
        }
      } catch {
        clearInterval(interval);
        setPolling(prev => ({ ...prev, [scriptKey]: false }));
      }
    }, 15000); // Poll every 15 seconds
  };

  const checkStatus = async (scriptKey) => {
    const video = videos[scriptKey];
    if (!video?.video_id) return;
    try {
      const res = await api.get(`/heygen/status/${video.video_id}`);
      setVideos(prev => ({ ...prev, [scriptKey]: { ...res.data.video, script_key: scriptKey } }));
      if (res.data.video?.status === 'completed') {
        toast.success('Video is ready!');
        try { await api.post(`/heygen/apply-video/${scriptKey}`); } catch {}
      } else {
        toast.info(`Status: ${res.data.video?.status}`);
      }
    } catch (err) {
      toast.error('Failed to check status');
    }
  };

  const toggleSelect = (key) => {
    setSelectedKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const selectAll = () => {
    const ungenerated = scripts.filter(s => !videos[s.key]?.video_url).map(s => s.key);
    setSelectedKeys(ungenerated);
  };

  const getScriptGroup = (key) => {
    if (key.startsWith('orientation_')) return 'Orientation';
    if (key.startsWith('week1_')) return 'Week 1';
    if (key.startsWith('week2_')) return 'Week 2';
    if (key.startsWith('week3_')) return 'Week 3';
    if (key.startsWith('week4_')) return 'Week 4';
    if (key.startsWith('coach_')) return 'Coach\'s Corner';
    return 'Other';
  };

  const grouped = scripts.reduce((acc, s) => {
    const group = getScriptGroup(s.key);
    if (!acc[group]) acc[group] = [];
    acc[group].push(s);
    return acc;
  }, {});

  const completedCount = scripts.filter(s => videos[s.key]?.status === 'completed' || videos[s.key]?.video_url).length;
  const processingCount = scripts.filter(s => videos[s.key]?.status === 'processing' || polling[s.key]).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-16" data-testid="heygen-admin">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Film className="w-8 h-8 text-orange-500" />
            <h1 className="text-3xl font-black text-white" data-testid="heygen-admin-title">HeyGen Video Manager</h1>
          </div>
          <p className="text-gray-400">Generate AI avatar videos for Academy courses. Videos auto-replace YouTube placeholders.</p>
        </div>

        {/* Prompt Studio — LLM-driven structured script generator */}
        <PromptStudio />

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-white">{scripts.length}</div>
              <div className="text-xs text-gray-500">Total Scripts</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{completedCount}</div>
              <div className="text-xs text-gray-500">Videos Ready</div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-400">{processingCount}</div>
              <div className="text-xs text-gray-500">Processing</div>
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <Button
            onClick={() => setBatchMode(!batchMode)}
            variant={batchMode ? 'default' : 'outline'}
            className={batchMode ? 'bg-orange-600 hover:bg-orange-700' : 'border-gray-700 text-gray-300'}
            data-testid="batch-mode-toggle"
          >
            <Zap className="w-4 h-4 mr-2" />
            {batchMode ? 'Exit Batch Mode' : 'Batch Generate'}
          </Button>

          {batchMode && (
            <>
              <Button onClick={selectAll} variant="outline" className="border-gray-700 text-gray-300" data-testid="select-all-btn">
                Select All Ungenerated
              </Button>
              <Button
                onClick={generateBatch}
                className="bg-green-600 hover:bg-green-700"
                disabled={selectedKeys.length === 0}
                data-testid="generate-batch-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Generate {selectedKeys.length} Videos
              </Button>
            </>
          )}

          <Button onClick={loadData} variant="outline" className="border-gray-700 text-gray-300 ml-auto" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Script Groups */}
        {Object.entries(grouped).map(([group, groupScripts]) => (
          <div key={group} className="mb-8">
            <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Video className="w-5 h-5 text-orange-500" />
              {group}
              <span className="text-sm font-normal text-gray-500">
                ({groupScripts.filter(s => videos[s.key]?.video_url).length}/{groupScripts.length} ready)
              </span>
            </h2>

            <div className="space-y-2">
              {groupScripts.map(script => {
                const video = videos[script.key];
                const isComplete = video?.status === 'completed' || video?.video_url;
                const isProcessing = video?.status === 'processing' || polling[script.key];
                const isGenerating = generating[script.key];
                const StatusIcon = STATUS_ICONS[isComplete ? 'completed' : isProcessing ? 'processing' : 'pending'];

                return (
                  <Card key={script.key} className={`bg-gray-900 border ${isComplete ? 'border-green-800/50' : 'border-gray-800'}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        {batchMode && !isComplete && (
                          <input
                            type="checkbox"
                            checked={selectedKeys.includes(script.key)}
                            onChange={() => toggleSelect(script.key)}
                            className="w-4 h-4 accent-orange-500"
                            data-testid={`checkbox-${script.key}`}
                          />
                        )}
                        
                        <StatusIcon className={`w-5 h-5 flex-shrink-0 ${STATUS_COLORS[isComplete ? 'completed' : isProcessing ? 'processing' : 'pending']} ${isProcessing ? 'animate-spin' : ''}`} />
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium text-sm">{script.title}</span>
                            <span className="text-gray-600 text-xs font-mono">{script.key}</span>
                          </div>
                          {isComplete && video?.video_url && (
                            <a href={video.video_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:underline truncate block">
                              {video.video_url.substring(0, 60)}...
                            </a>
                          )}
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => setExpandedScript(expandedScript === script.key ? null : script.key)}
                            className="text-gray-500 hover:text-white p-1"
                          >
                            {expandedScript === script.key ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>

                          {isComplete ? (
                            <Button size="sm" variant="outline" onClick={() => checkStatus(script.key)} className="border-green-800 text-green-400 text-xs" data-testid={`status-${script.key}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Ready
                            </Button>
                          ) : isProcessing ? (
                            <Button size="sm" variant="outline" onClick={() => checkStatus(script.key)} className="border-yellow-800 text-yellow-400 text-xs" data-testid={`status-${script.key}`}>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processing
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => generateSingle(script.key)}
                              disabled={isGenerating}
                              className="bg-orange-600 hover:bg-orange-700 text-xs"
                              data-testid={`generate-${script.key}`}
                            >
                              {isGenerating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                              Generate
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Expanded script preview */}
                      {expandedScript === script.key && (
                        <div className="mt-3 p-3 bg-gray-800 rounded text-gray-300 text-sm leading-relaxed">
                          {CURRICULUM_SCRIPTS_FULL[script.key] || script.script_preview}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// We'll load full scripts from the API for preview; this is just a fallback
const CURRICULUM_SCRIPTS_FULL = {};

export default HeyGenAdmin;
